import { Theme } from '../../types/shared'
import tailwindColors from '../constants/colors'
import { getHEXWithSpecificAplha, hexStringToRGBA, rgbaToHexString } from '../utils/colors'
import { IThumbSettings } from './chart_settings/interfaces'

export interface IChartThemeColors {
  aftermarketText: string
  aftermarketMarker: string
  aftermarketFill: string
  background: string
  borderDown: string
  borderUp: string
  canvasFill: string
  cross: string
  crossText: string
  crossTextBackground: string
  currentBackground: string
  currentText: string
  grid: string
  gridSecondary: string
  indicatorCurrentBackground: string
  indicatorCurrentText: string
  line: string
  marketDown: string
  marketUp: string
  negativeChange: string
  ohlcTrendDown: string
  ohlcTrendUp: string
  overlayTransparentBackground: string
  percentageZeroLineText: string
  performanceIndicatorLabel: string
  positiveChange: string
  premarketFill: string
  prevClose: string
  text: string
  textSecondary: string
  trendDown: string
  trendUp: string
  volumeTrendDown: string
  volumeTrendUp: string
  wickDown: string
  wickUp: string
  zeroChange: string
}

export interface IIndicatorThemeColors {
  line: string
  signalLine: string
}

export interface IThumbColors {
  borderColor: string
  borderColorActive: string
}

export interface IElementThemeColors {
  line: string
  border: string
  textWithoutBackground: string
}

interface IDarkerWickColors {
  borderUp: string
  wickUp: string
  borderDown: string
  wickDown: string
}

export const chartLightTheme: IChartThemeColors = {
  aftermarketText: tailwindColors.black,
  aftermarketMarker: tailwindColors.violet[200],
  aftermarketFill: '#5e36b81f',
  background: '#008CFF0E',
  borderDown: '#000000FF',
  borderUp: '#000000FF',
  canvasFill: '#FFFFFFFF',
  cross: '#0000004C',
  crossText: '#fff',
  crossTextBackground: '#111',
  currentBackground: tailwindColors.brightYellow[100],
  currentText: tailwindColors.gray[800],
  grid: '#D2D2D2FF',
  gridSecondary: '#ECECEC',
  indicatorCurrentBackground: tailwindColors.gray[600],
  indicatorCurrentText: '#fff',
  line: '#1E6DC0FF',
  marketDown: tailwindColors.red[500],
  marketUp: tailwindColors.green[500],
  negativeChange: '#C80000FF',
  ohlcTrendDown: '#E54040FF',
  ohlcTrendUp: '#40A140FF',
  overlayTransparentBackground: getHEXWithSpecificAplha('#C9CCD6', 0.25),
  percentageZeroLineText: '#111',
  performanceIndicatorLabel: tailwindColors.gray[600],
  positiveChange: '#009600FF',
  premarketFill: '#fbbb581f',
  prevClose: '#C80000FF',
  text: '#828282',
  textSecondary: '#dedede',
  trendDown: '#FF0000FF',
  trendUp: '#00FF00FF',
  volumeTrendDown: '#ffc0c0',
  volumeTrendUp: '#a8e0a8',
  wickDown: '#000000FF',
  wickUp: '#000000FF',
  zeroChange: '#000',
}

export const chartDarkTheme: IChartThemeColors = {
  aftermarketText: tailwindColors.white,
  aftermarketMarker: tailwindColors.violet[400],
  aftermarketFill: '#40297930',
  background: '#1E6DC019',
  borderDown: '#F63538FF',
  borderUp: '#30CC5AFF',
  canvasFill: tailwindColors.gray[800],
  cross: '#404553',
  crossText: '#fff',
  crossTextBackground: '#111',
  currentBackground: tailwindColors.yellow[200],
  currentText: tailwindColors.gray[700],
  grid: '#363A46FF',
  gridSecondary: '#2C303A',
  indicatorCurrentBackground: tailwindColors.gray[500],
  indicatorCurrentText: '#fff',
  line: '#1E6DC0FF',
  marketDown: tailwindColors.red[300],
  marketUp: tailwindColors.green[300],
  negativeChange: tailwindColors.red[400],
  ohlcTrendDown: '#E54040FF',
  ohlcTrendUp: '#40A140FF',
  overlayTransparentBackground: getHEXWithSpecificAplha('#0C0E13', 0.35),
  percentageZeroLineText: '#fff',
  performanceIndicatorLabel: tailwindColors.gray[300],
  positiveChange: tailwindColors.green[400],
  premarketFill: '#95660016',
  prevClose: '#C80000FF',
  text: '#929cb3',
  textSecondary: '#929CB399',
  trendDown: '#F63538FF',
  trendUp: '#30CC5AFF',
  volumeTrendDown: '#F635387F',
  volumeTrendUp: '#30CC5A7F',
  wickDown: '#F63538FF',
  wickUp: '#30CC5AFF',
  zeroChange: '#929cb3',
}

export const darkerWickColors: IDarkerWickColors = {
  borderUp: '#006400FF',
  wickUp: '#006400',
  borderDown: '#6E0000FF',
  wickDown: '#6E0000FF',
}

export const indicatorLightTheme: IIndicatorThemeColors = {
  line: '#000000',
  signalLine: tailwindColors.red[400],
}

export const indicatorDarkTheme: IIndicatorThemeColors = {
  line: '#D07933',
  signalLine: tailwindColors.red[400],
}

export const elementTheme: IElementThemeColors = {
  line: tailwindColors.blue[300],
  border: tailwindColors.blue[300],
  textWithoutBackground: tailwindColors.blue[300],
}

export const thumbLightTheme: IThumbColors = {
  borderColor: rgbaToHexString(
    {
      ...hexStringToRGBA(tailwindColors.blue[300]),
      a: 0.5,
    },
    true
  ),
  borderColorActive: tailwindColors.blue[300],
}

export const thumbDarkTheme: IThumbColors = {
  ...thumbLightTheme,
}

export const generalColors: Array<string> = [
  '#1f77b4',
  '#ff7f0e',
  '#2ca02c',
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#7f7f7f',
  '#bcbd22',
  '#17becf',
]

export const getGeneralThumbSettings = (theme: Theme): IThumbSettings => ({
  size: 9, // MUST BE ODD NUMBER for propper centering without blur
  touchSize: 21, // MUST BE ODD NUMBER for propper centering without blur
  borderWidth: 1,
  touchBorderWidth: 3,
  hitboxOffset: 2,
  ...(theme === Theme.light ? thumbLightTheme : thumbDarkTheme),
})

// node chart with thin bars color override
export const thinBarColorsOverride = {
  trendUp: '#00b400',
  trendDown: '#b40000',
}

export enum AutoSaveState {
  Off,
  Saved,
  Unsaved,
  Saving,
}

export const CHART_EVENT_BADGE_SIZE = 24

export const CHART_BARS_BUFFER_IN_PX = 100

export const DEFAULT_LABEL_COLOR = '#0587cd'
export const DEFAULT_CLONE_OFFSET = 10
